import React, { useEffect, useState } from "react";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useLocation } from "react-router-dom";

import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import QuotationDataMiddle from "./QuotationDataMiddle";
import ModernLoader from "../../../../components/Loader/ModernLoader";
import { createSoftwareQuotation } from "../../../../services/quotationService";

const DownloadSoftwareQuotation = () => {
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [shouldPrint, setShouldPrint] = useState(false);

  const { clientName, profileData, reportData } = state || {};

  const handlePrint = async () => {
    const payload = {};

    // 1. Client & General Details
    if (reportData?.clientName) {
      payload.client_name = reportData.clientName;
    }

    if (reportData?.softwareType) {
      payload.software_type = reportData.softwareType;
    }

    if (reportData?.quotationDate) {
      payload.quotation_date = reportData.quotationDate;
    }

    if (reportData?.domainManagedBy) {
      payload.domain_managed_by = reportData.domainManagedBy;
    }

    // 2. IDSPay Integration
    payload.has_idspay_api = reportData?.idsPayIntegration === "yes";

    if (reportData?.idsPayIntegration === "yes") {
      payload.idspay_apis = (reportData?.idsApi || []).map((item) => ({
        api_service_name: item.service,
        api_call_charge: Number(item.charge) || 0,
        integration_fee: Number(item.fee) || 0,
      }));

      payload.idspay_api_integration_cost = Number(reportData?.idsPayCost) || 0;
    }

    // 3. Third-Party API Integration
    payload.has_third_party_api = reportData?.thirdPartyIntegration === "yes";

    if (reportData?.thirdPartyIntegration === "yes") {
      payload.third_party_apis = (reportData?.thirdPartyApis || []).map(
        (item) => ({
          api_service_name: item.apiName,
          integration_fee: Number(item.amount) || 0,
        }),
      );

      payload.third_party_api_integration_cost =
        Number(reportData?.thirdPartyCost) || 0;
    }

    // 4. Login Types
    if (reportData?.selectedLogins?.length > 0) {
      payload.login_types = reportData.selectedLogins;
    }

    // 5. Software Cost
    if (reportData?.softwareCost) {
      payload.software_cost = Number(reportData.softwareCost) || 0;
    }

    // 6. Discount
    if (reportData?.discount) {
      payload.discount_applicable = Number(reportData.discount) || 0;
    }

    // 7. AMC
    if (reportData?.amcCost) {
      payload.annual_maintenance_cost = Number(reportData.amcCost) || 0;
    }

    // 8. Software Delivery Date
    if (reportData?.deliveryDate) {
      payload.software_delivery_date = reportData.deliveryDate;
    }

    // 9. Payment Terms
    if (reportData?.paymentModes?.length > 0) {
      const validPayments = reportData.paymentModes.filter(
        (payment) =>
          payment?.remark?.trim() !== "" ||
          payment?.amount?.toString().trim() !== "",
      );

      if (validPayments.length > 0) {
        payload.payment_terms = validPayments.map((payment) => ({
          term_title: payment.remark,
          amount: Number(payment.amount) || 0,
        }));
      }
    }

    // 10. Quotation Details
    if (reportData?.quillContent) {
      payload.quotation_details = reportData.quillContent;
    }

    console.log("Quotation API Payload:", payload);

    try {
      setLoading(true);

      const res = await createSoftwareQuotation(payload);

      if (res?.status?.code === 200 || res?.data?.status?.code === 200) {
        showSuccessToast(
          res?.data?.message || res?.data?.data?.message || res?.message,
        );
        setLoading(false);
        setShouldPrint(true);
      } else {
        showErrorToast(
          res?.message || res?.data?.message || res?.error?.message,
        );
      }
    } catch (error) {
      console.log(error);

      showErrorToast(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while creating quotation.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && shouldPrint) {
      setShouldPrint(false);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.print();
        });
      });
    }
  }, [loading, shouldPrint]);

  // const maxPlans = Math.max(
  //   ...reportData.map((item) => item?.system_prices?.length || 0),
  // );

  // useEffect(() => {
  //   console.log(maxPlans);
  // }, [maxPlans]);

  return (
    <div
      // style={{ WebkitFontSmoothing: "antialiased" }}
      // id="pdf-content"
      className="bg-gray-100 py-10 min-h-screen font-sans"
    >
      <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap');

  @media print {

    @page {
      size: A4;
      margin: 10mm;
    }

    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
      width: 100% !important;
    }

    body {
      font-family: 'Inter', sans-serif;
    }

    /* Hide buttons / controls */
    .no-print {
      display: none !important;
    }

    /* Remove screen shadows */
    .container-shadow {
      box-shadow: none !important;
      border: 1px solid #e5e7eb !important;
    }

    /* VERY IMPORTANT:
       Prevent screen heights from creating blank areas */
    .print-container,
    .print-container > div,
    .print-container section {
      height: auto !important;
      min-height: 0 !important;
    }

      .no-print {
    display: none !important;
  }

    /* Don't allow unnecessary horizontal overflow */
    .print-container {
      width: 100% !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    /* Keep sections together where possible */
    .print-section,
    .print-card {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    /* Keep headings with their content */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      break-after: avoid;
      page-break-after: avoid;
    }

    /* Tables */
    table {
      width: 100% !important;
      border-collapse: collapse !important;
    }

    tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    /* Images */
    img {
      max-width: 100% !important;
    }

    /* Only use this where you explicitly want a new page */
    .page-break {
      break-after: page;
      page-break-after: always;
    }
  }
`}</style>

      {loading && (
        <div className="no-print">
          <ModernLoader />
        </div>
      )}

      {/* PAGE 1 */}
      <div className="max-w-[800px] mx-auto bg-white shadow-2xl mb-10 container-shadow print-page overflow-hidden">
        {/* Hero Section */}
        <div
          className="relative p-8 text-white overflow-hidden"
          style={{
            background: "linear-gradient(90deg, #020617 0%, #1e293b 100%)",
          }}
        >
          <div className="flex justify-between items-start relative z-10">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/img/idspay_logo_log2.png"
                alt=""
                className="w-[200px] h-auto"
              />
            </div>
            <div className="text-right">
              <h2 className="text-4xl font-black tracking-tighter text-cyan-400">
                THE FUTURE
              </h2>
              <h2 className="text-4xl font-black tracking-tighter">
                IS TECHNOLOGY
              </h2>
            </div>
          </div>
          <div className="flex justify-between mt-12 text-[10px] font-bold text-orange-400 z-10 relative">
            <a
              href="https://apiuser.idspay.in/SignUpForm"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Create API Account
            </a>
            <a
              href="https://idspay.in/api-library"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              API Marketplace
            </a>
            <a
              href="https://idspay.in"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              www.idspay.in
            </a>
          </div>
          {/* Cyber Grid Pattern */}
          <div
            className="absolute bottom-0 left-0 w-full h-24 opacity-20"
            style={{
              backgroundImage: "radial-gradient(#3b82f6 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          ></div>
        </div>

        {/* Company Name Bar */}
        <div className="bg-blue-800 text-white p-4 px-8 font-bold text-xl">
          Company name:- {clientName}
        </div>

        {/* About Section */}
        <div className="flex min-h-[400px]">
          <div
            className="w-1/3 p-6 text-white font-bold text-xl"
            style={{
              background: "linear-gradient(180deg, #1e3a8a 0%, #172554 100%)",
            }}
          >
            About :
          </div>
          <div className="w-2/3 p-8 text-sm text-gray-800 leading-relaxed">
            <p className="mb-4">
              <strong>IDSPay</strong> is a fast-growing{" "}
              <strong>Fintech & API Service Provider</strong> delivering secure,
              scalable and real-time verification solutions across India having{" "}
              <strong>3000+</strong> Client base (100+ NBFCs).
            </p>
            <p className="font-bold mb-4">We specialize in:</p>
            <ul className="space-y-2 list-disc pl-5">
              <li>
                Identity Verification (KYC, PAN, Aadhaar, Voter ID, Passport,
                Driving License, etc.)
              </li>
              <li>
                Financial Risk Checks (Bank Verification, Mobile to UPI, Udyam,
                GST, CIN, Credit Bureau, etc.)
              </li>
              <li>Address & Geo-Location Validation</li>
              <li>Video KYC (RBI Compliant) + Face API</li>
              <li>Digi Locker Integrations</li>
              <li>
                Background Verification and Fraud Prevention & Many more (200+
                API's).
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t border-gray-200 text-sm">
          <div className="flex border-b border-gray-200">
            <div className="w-1/3 bg-blue-900 text-white p-3 px-6 font-bold">
              Phone Number:
            </div>
            <div className="w-2/3 p-3 px-6 text-blue-900 font-semibold">
              {profileData?.mobile_number}
            </div>
          </div>
          <div className="flex border-b border-gray-200">
            <div className="w-1/3 bg-blue-900 text-white p-3 px-6 font-bold">
              Email Address:
            </div>
            <div className="w-2/3 p-3 px-6 text-gray-600">
              {profileData?.email_address}
            </div>
          </div>
          <div className="flex">
            <div className="w-1/3 bg-blue-900 text-white p-3 px-6 font-bold">
              Website:
            </div>
            <div className="w-2/3 p-3 px-6 flex justify-between">
              <a
                href="https://idspay.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline cursor-pointer"
              >
                www.idspay.in
              </a>
              <a
                href="https://idspay.in/api-library"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline cursor-pointer"
              >
                Visit API Marketplace
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* PAGE 2 */}
      <div className="max-w-[800px] hidden mx-auto bg-white shadow-2xl p-10 mb-10 container-shadow print-page">
        <table className="w-full border-collapse border border-gray-300 text-[11px]">
          <thead>
            <tr className="bg-blue-400 text-black font-bold uppercase">
              <th className="border border-gray-300 p-2 w-1/4">Category</th>
              <th className="border border-gray-300 p-2 w-1/4">Product Name</th>
              <th className="border border-gray-300 p-2 w-1/4"></th>

              <th colSpan="3" className="border border-gray-300 p-2">
                Per verification Cost (in INR)
              </th>
            </tr>
            <tr className="text-blue-700">
              <th className="border border-gray-300 p-2 underline italic">
                API Category
              </th>
              <th className="border border-gray-300 p-2 underline italic">
                API Name
              </th>
              <th className="border border-gray-300 p-2 underline italic">
                Monthly Volume
              </th>
              {/* <th className="border border-gray-300 p-2 italic text-purple-800">
                per / hit
              </th> */}
            </tr>
          </thead>
          <tbody className="text-center font-semibold text-gray-700">
            {/* {reportData?.map((item, i) => (
              <tr className="hover:bg-slate-50 transition">
                <td className="p-4 border border-slate-200 font-bold text-blue-800">
                  {item?.category}
                </td>
                <td className="p-4 border border-slate-200 font-bold text-blue-800">
                  {item?.service_name}
                </td>

                <td className="p-4 border border-slate-200 font-bold text-blue-800">
                  {item?.monthly_volume}
                </td>

                {item?.system_prices?.map((plan, i) => {
                  return (
                    <td
                      key={i}
                      className={`p-4 border border-slate-200  text-center align-middle font-mono font-bold text-green-600 ${item ? "text-green-600" : "text-red-600"}`}
                    >
                      {`₹${plan}`}
                    </td>
                  );
                })}
              </tr>
            ))} */}
          </tbody>
        </table>

        <div className="mt-8 text-[11px]">
          <p className="text-blue-800 font-bold underline mb-2">Other Terms:</p>
          <ol className="list-decimal pl-4 space-y-2 text-gray-600">
            <li>
              Prices are exclusive of applicable Government levies and taxes.
            </li>
            <li>
              Delivery of the service will be through APIs to be consumed by you
              in your application / website.
            </li>
            <li>
              "Per Transaction" means every request for KYC Validation is called
              through the API. Charges will be levied only for requests with a
              successful response (Status "200" or "422").
            </li>
          </ol>
        </div>

        <div className="mt-10">
          <h3 className="font-black text-lg flex items-center mb-4">
            <span className="mr-2">💡</span> Why work with IDSPay?
          </h3>
          <ul className="space-y-2 font-bold text-sm text-gray-800">
            <li className="flex items-center">
              <span className="text-green-600 mr-2">✓</span> Pan-India API
              Coverage
            </li>
            <li className="flex items-center">
              <span className="text-green-600 mr-2">✓</span> Direct Integration
              with Official Gov Sources
            </li>
            <li className="flex items-center">
              <span className="text-green-600 mr-2">✓</span> High Accuracy &
              Real-Time Validation
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-[800px]  mx-auto bg-white shadow-2xl p-10  mb-10 container-shadow print-page">
        {reportData && <QuotationDataMiddle reportData={reportData} />}
      </div>

      {/* PAGE 3 */}
      <div className="max-w-[800px] mx-auto bg-white shadow-2xl p-10 container-shadow mb-10 print-page">
        <ul className="space-y-3 font-bold text-sm mb-12 text-gray-800">
          <li className="flex items-center">
            <span className="text-green-600 mr-2">✓</span> No Manual Data Entry
            / Zero Paperwork
          </li>
          <li className="flex items-center">
            <span className="text-green-600 mr-2">✓</span> Dedicated Technical &
            Sales Support
          </li>
          <li className="flex items-center">
            <span className="text-green-600 mr-2">✓</span> Fast Integration - Go
            Live in Hours
          </li>
        </ul>

        <div className="flex border border-gray-300 text-sm">
          <div className="w-1/3">
            <div className="bg-blue-900 text-white p-3 font-bold border-b border-gray-300">
              Duration:
            </div>
            <div className="bg-blue-400 p-6 h-32 flex items-center justify-center text-center font-bold text-blue-900 leading-tight">
              Proposal is valid for 3 days from the date of creation.
            </div>
            <div className="bg-blue-800 h-10 border-b border-gray-300"></div>
            <div className="bg-blue-400 h-10"></div>
          </div>
          <div className="w-2/3">
            <div className="bg-blue-900 text-white p-3 font-bold border-b border-gray-300 text-center">
              Scope of Work:
            </div>
            <div className="bg-slate-50 p-8 h-[212px] flex flex-col justify-center">
              <ul className="space-y-2 font-semibold text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span> Ready-to-use APIs eliminate
                  development time.
                </li>
                <li className="flex items-start">
                  <span class="mr-2">•</span> Quick onboarding and product
                  launch within days.
                </li>
                <li className="flex items-start">
                  <span class="mr-2">•</span> No need to maintain internal tech
                  resources.
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span> Pay only for API consumption
                  (per-hit model).
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 border-l-4 border-blue-900 pl-4">
          <p className="text-sm font-bold underline mb-2">Disclaimer:</p>
          <p className="text-[11px] text-gray-500 italic leading-relaxed">
            This document is for the sole use of the intended recipient(s) and
            contains confidential and privileged information. Any unauthorized
            review, use, disclosure, copying, or distribution is strictly
            prohibited.
          </p>
        </div>

        <div className="w-full text-sm mt-14 flex gap-2 justify-end">
          <strong>Proposal Date-</strong>
          {new Date().toLocaleDateString()}
        </div>

        <div className="mt-26 flex flex-col items-center opacity-60">
          <div className="flex items-center space-x-2 grayscale">
            <div className="text-center">
              <img
                src="/assets/img/idspay_logo_log3.png"
                alt=""
                className="w-[150px] h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Print Button */}
      <div className="text-center mt-10 no-print">
        <button
          type="button"
          onClick={handlePrint}
          className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-bold shadow-xl transition-all transform hover:scale-105"
        >
          Print as Official PDF
        </button>
      </div>
    </div>
  );
};

export default DownloadSoftwareQuotation;
